import React from 'react';
import { AlertTriangle, Trash2, Shield, AlertCircle, Users, X } from 'lucide-react';
import ModalWrapper from './ModalWrapper';
import styles from '../CSS/UserModals.module.css';

const UserModals = ({
  showDeleteModal,
  userToDelete,
  showBulkRoleModal,
  bulkRoleChange,
  selectedUsersCount,
  roles,
  getRoleName,
  onDeleteConfirm,
  onDeleteCancel,
  onBulkRoleChange,
  onBulkRoleCancel,
  setBulkRoleChange
}) => {
  return (
    <>
      {/* Modal de confirmación para eliminar */}
      <ModalWrapper 
        isOpen={showDeleteModal} 
        onClose={onDeleteCancel}
        className="medium"
      >
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalIconTitle}>
              <div className={styles.iconContainer}>
                <AlertTriangle size={24} className={styles.warningIcon} />
              </div>
              <h3 className={styles.modalTitle}>
                ¿Confirmar eliminación?
              </h3>
            </div>
            <button 
              className={styles.closeButton}
              onClick={onDeleteCancel}
              aria-label="Cerrar modal"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className={styles.modalContent}>
            <div className={styles.userInfo}>
              <div className={styles.userDetails}>
                <p className={styles.confirmText}>
                  Esta acción eliminará permanentemente el usuario de la base de datos local:
                </p>
                <div className={styles.userCard}>
                  <div className={styles.userAvatar}>
                    {userToDelete?.firstName?.[0]}{userToDelete?.lastName?.[0]}
                  </div>
                  <div className={styles.userMeta}>
                    <div className={styles.userName}>
                      {userToDelete?.firstName} {userToDelete?.lastName}
                    </div>
                    <div className={styles.userId}>ID: {userToDelete?.id}</div>
                  </div>
                </div>
              </div>
              
              {userToDelete?.syncedFromWorkspace && (
                <div className={styles.workspaceWarning}>
                  <AlertCircle size={18} className={styles.warningIconSmall} />
                  <div className={styles.warningContent}>
                    <strong>Nota importante:</strong>
                    <p>Este usuario fue sincronizado desde Google Workspace. El usuario seguirá existiendo en Workspace y podrá volver a sincronizarse. Solo se eliminará de la base de datos local.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className={styles.modalFooter}>
            <button
              className={`${styles.modalButton} ${styles.cancelButton}`}
              onClick={onDeleteCancel}
            >
              Cancelar
            </button>
            <button
              className={`${styles.modalButton} ${styles.deleteButton}`}
              onClick={onDeleteConfirm}
            >
              <Trash2 size={16} />
              Eliminar de BD
            </button>
          </div>
        </div>
      </ModalWrapper>

      {/* Modal de cambio de rol en lote */}
      <ModalWrapper 
        isOpen={showBulkRoleModal} 
        onClose={onBulkRoleCancel}
        className="medium"
      >
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalIconTitle}>
              <div className={styles.iconContainer}>
                <Shield size={24} className={styles.shieldIcon} />
              </div>
              <h3 className={styles.modalTitle}>
                Cambiar rol en lote
              </h3>
            </div>
            <button 
              className={styles.closeButton}
              onClick={onBulkRoleCancel}
              aria-label="Cerrar modal"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className={styles.modalContent}>
            <div className={styles.bulkInfo}>
              <div className={styles.bulkInfoIcon}>
                <Users size={18} />
              </div>
              <div className={styles.bulkInfoContent}>
                <span className={styles.bulkCount}>{selectedUsersCount}</span>
                <span className={styles.bulkText}>usuarios seleccionados</span>
              </div>
            </div>
            
            <p className={styles.description}>
              Seleccione el nuevo rol para los usuarios seleccionados.
              Solo se actualizarán los usuarios que están en la base de datos local.
            </p>

            <div className={styles.securityNote}>
              <AlertCircle size={16} className={styles.securityIcon} />
              <div className={styles.securityContent}>
                <strong>Nota de seguridad:</strong>
                <span>Tu cuenta actual será omitida automáticamente por seguridad.</span>
              </div>
            </div>
            
            <div className={styles.roleSelectContainer}>
              <label className={styles.roleLabel}>
                <Shield size={16} />
                Nuevo rol
              </label>
              <div className={styles.selectWrapper}>
                <select
                  value={bulkRoleChange}
                  onChange={(e) => setBulkRoleChange(e.target.value)}
                  className={styles.roleSelect}
                >
                  <option value="">Seleccione un rol</option>
                  <option value="admin">Administrador</option>
                  <option value="user">Usuario</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {bulkRoleChange && (
              <div className={styles.rolePreview}>
                <div className={styles.previewIcon}>
                  <Shield size={16} />
                </div>
                <div className={styles.previewContent}>
                  <strong>Nuevo rol:</strong> {getRoleName(bulkRoleChange)}
                </div>
              </div>
            )}
          </div>
          
          <div className={styles.modalFooter}>
            <button
              className={`${styles.modalButton} ${styles.cancelButton}`}
              onClick={onBulkRoleCancel}
            >
              Cancelar
            </button>
            <button
              className={`${styles.modalButton} ${styles.confirmButton}`}
              onClick={onBulkRoleChange}
              disabled={!bulkRoleChange}
            >
              <Shield size={16} />
              <span className={styles.buttonText}>
                Cambiar rol
                <span className={styles.buttonCount}>({selectedUsersCount})</span>
              </span>
            </button>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default UserModals;